import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { TouchDial } from '../components/custom/TouchDial';
import { BottomNav } from '../components/custom/BottomNav';
import { TaskCard } from '../components/custom/TaskCard';
import { VoiceInputInline } from '../components/custom/VoiceInput';
import OfflineIndicator from '../components/custom/OfflineIndicator';
import OnboardingTutorial from '../components/custom/OnboardingTutorial';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { scheduleAllReminders, checkUrgentDeadlines } from '../utils/notifications';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, isSameDay, isToday } from 'date-fns';
import { Plus, Timer, AlertTriangle, Clock, ChevronRight, ChevronDown, LogOut, Gift, Shield, CheckSquare, Square, ListTodo, Trash2, Calendar as CalendarIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TASK_TYPES = [
    {
        code: "L100",
        name: "Case Analysis & Strategy",
        description: "Analysis of case facts and applicable law to assess issues and develop strategy."
    },
    {
        code: "L110",
        name: "Legal Research",
        description: "Research and analysis of statutes, case law, regulations, and related legal authorities."
    },
    {
        code: "L120",
        name: "Document Drafting & Review",
        description: "Drafting, reviewing, and revising pleadings, motions, agreements, correspondence, and other documents."
    },
    {
        code: "L130",
        name: "Discovery & Evidence Review",
        description: "Preparation, review, and analysis of discovery materials, documents, and evidence."
    },
    {
        code: "L140",
        name: "Witness & Deposition Preparation",
        description: "Preparation for and participation in witness interviews, depositions, and expert-related activities."
    },
    {
        code: "L150",
        name: "Court Appearances & Proceedings",
        description: "Preparation for and attendance at court hearings, proceedings, trials, and oral arguments."
    },
    {
        code: "L160",
        name: "Negotiation & Resolution Efforts",
        description: "Negotiation with opposing parties and participation in settlement or alternative dispute resolution proceedings."
    },
    {
        code: "L170",
        name: "Client & Third-Party Communications",
        description: "Communications with the client, opposing counsel, court personnel, experts, and other third parties regarding the matter."
    },
    {
        code: "L180",
        name: "Case Management & Administration",
        description: "Case organization, scheduling, coordination, supervision, and related administrative activities necessary to advance the matter."
    },
    {
        code: "L190",
        name: "Travel & Waiting Time",
        description: "Travel and waiting time related to court appearances, depositions, meetings, or other matter-related activities."
    },
    {
        code: "L300",
        name: "Investigations & Compliance Review",
        description: "Conducting interviews, reviewing records, analyzing findings, and preparing reports in connection with an internal investigation or compliance review."
    }
];

// Helper to get display label for task type
const getTaskLabel = (task) => `${task.code} - ${task.name}`;
const getTaskByCode = (code) => TASK_TYPES.find(t => t.code === code) || TASK_TYPES[0];

// Default fallback case types (alphabetically sorted)
const DEFAULT_CASE_TYPES = [
    "Bankruptcy",
    "Civil Rights",
    "Contract Dispute",
    "Criminal Defense",
    "Family Law",
    "Immigration",
    "Intellectual Property",
    "Labor & Employment",
    "Personal Injury",
    "Real Estate",
    "Other"
];

export default function Dashboard() {
    const [cases, setCases] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
    const [dialMinutes, setDialMinutes] = useState(60);
    const [activeTimer, setActiveTimer] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    
    // Onboarding tutorial
    const [showTutorial, setShowTutorial] = useState(false);
    
    // Quick add state
    const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
    const [quickAddType, setQuickAddType] = useState('case'); // 'case', 'time', 'reminder', 'todo'
    
    // Create case
    const [newCaseName, setNewCaseName] = useState('');
    const [newClientName, setNewClientName] = useState('');
    const [newCaseType, setNewCaseType] = useState('Other');
    const [caseTypes, setCaseTypes] = useState(DEFAULT_CASE_TYPES);
    
    // Manual time
    const [selectedCase, setSelectedCase] = useState('');
    const [selectedTask, setSelectedTask] = useState(TASK_TYPES[0].code);
    const [manualNotes, setManualNotes] = useState('');
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Complimentary subscription popup
    const [showComplimentaryPopup, setShowComplimentaryPopup] = useState(false);
    const [complimentaryEndDate, setComplimentaryEndDate] = useState(null);
    
    // Admin check
    const [isAdmin, setIsAdmin] = useState(false);
    
    // Todo state
    const [showTodos, setShowTodos] = useState(false);
    const [todos, setTodos] = useState([]);
    const [todoCounts, setTodoCounts] = useState({ case_counts: {}, total: 0 });
    const [isLoadingTodos, setIsLoadingTodos] = useState(false);
    const [newTodoTitle, setNewTodoTitle] = useState('');
    const [newTodoCase, setNewTodoCase] = useState('');
    const [newTodoPriority, setNewTodoPriority] = useState('medium');
    const [newTodoDueDate, setNewTodoDueDate] = useState('');
    
    // Complete todo with time conversion
    const [completeTodoModal, setCompleteTodoModal] = useState(null);
    const [convertToTime, setConvertToTime] = useState(false);
    const [todoTimeMinutes, setTodoTimeMinutes] = useState(30);
    const [todoTaskType, setTodoTaskType] = useState(TASK_TYPES[0].code);
    const [todoNote, setTodoNote] = useState('');
    const [todoDate, setTodoDate] = useState(new Date().toISOString().split('T')[0]);
    
    const { getAuthHeaders, logout, user } = useAuth();
    const navigate = useNavigate();
    
    // Offline sync hook
    const { 
        isOnline, 
        isSyncing, 
        pendingCount, 
        queueTimeEntry, 
        triggerSync,
        cacheForOffline 
    } = useOfflineSync(getAuthHeaders);

    const fetchCases = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/cases`, {
                headers: getAuthHeaders()
            });
            setCases(response.data);
            
            // Cache cases for offline use
            cacheForOffline(response.data);
            
            // Check for active timer
            const caseWithTimer = response.data.find(c => c.active_timer);
            if (caseWithTimer) {
                const entriesRes = await axios.get(`${API}/time-entries/active/${caseWithTimer.id}`, {
                    headers: getAuthHeaders()
                });
                if (entriesRes.data && entriesRes.data.active_timer) {
                    setActiveTimer({ ...entriesRes.data.active_timer, case_name: caseWithTimer.name });
                } else {
                    setActiveTimer(null);
                }
            } else {
                setActiveTimer(null);
            }
        } catch (error) {
            toast.error('Failed to load cases');
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders]);

    const fetchReminders = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/reminders/upcoming?days=30`, {
                headers: getAuthHeaders()
            });
            const fetchedReminders = response.data.reminders || [];
            setReminders(fetchedReminders);
            
            // Schedule push notifications for upcoming deadlines
            scheduleAllReminders(fetchedReminders);
            
            // Check for urgent deadlines (first load only)
            checkUrgentDeadlines(fetchedReminders);
        } catch (error) {
            console.error('Failed to load reminders');
        }
    }, [getAuthHeaders]);

    const fetchTodos = useCallback(async () => {
        setIsLoadingTodos(true);
        try {
            const [todosRes, countsRes] = await Promise.all([
                axios.get(`${API}/todos`, { headers: getAuthHeaders() }),
                axios.get(`${API}/todos/incomplete-counts`, { headers: getAuthHeaders() })
            ]);
            setTodos(todosRes.data);
            setTodoCounts(countsRes.data);
        } catch (error) {
            console.error('Failed to load todos');
        } finally {
            setIsLoadingTodos(false);
        }
    }, [getAuthHeaders]);

    const fetchCaseTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/case-types`, {
                headers: getAuthHeaders()
            });
            const types = response.data.case_types || [];
            // Sort alphabetically, keeping "Other" at the end
            const sortedTypes = types.filter(t => t !== 'Other').sort();
            if (types.includes('Other')) sortedTypes.push('Other');
            setCaseTypes(sortedTypes.length > 0 ? sortedTypes : DEFAULT_CASE_TYPES);
        } catch {
            setCaseTypes(DEFAULT_CASE_TYPES);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        fetchCases();
        fetchReminders();
        fetchTodos();
        fetchCaseTypes();
        
        // Check for complimentary subscription popup
        const checkComplimentary = async () => {
            try {
                const response = await axios.get(`${API}/subscription/status`, {
                    headers: getAuthHeaders()
                });
                if (response.data.show_complimentary_popup && response.data.is_complimentary) {
                    setShowComplimentaryPopup(true);
                    setComplimentaryEndDate(response.data.complimentary_end_date);
                }
            } catch (error) {
                console.error('Failed to check subscription status');
            }
        };
        
        // Check admin status
        const checkAdmin = async () => {
            try {
                const response = await axios.get(`${BACKEND_URL}/api/admin/check`, {
                    headers: getAuthHeaders()
                });
                setIsAdmin(response.data.is_admin);
            } catch (error) {
                // Not an admin or endpoint not available
            }
        };
        
        checkComplimentary();
        checkAdmin();
    }, [fetchCases, fetchReminders, getAuthHeaders]);

    // Check if onboarding tutorial should show
    useEffect(() => {
        const onboardingComplete = localStorage.getItem('voilaw_onboarding_complete');
        if (!onboardingComplete) {
            // Show tutorial for first-time users
            setShowTutorial(true);
        }
    }, []);

    // Also show tutorial if user has no cases (empty state)
    useEffect(() => {
        if (!isLoading && cases.length === 0) {
            const onboardingComplete = localStorage.getItem('voilaw_onboarding_complete');
            // Only show if not already dismissed
            if (!onboardingComplete) {
                setShowTutorial(true);
            }
        }
    }, [isLoading, cases.length]);

    // Timer effect
    useEffect(() => {
        if (activeTimer) {
            const startTime = new Date(activeTimer.start_time);
            const interval = setInterval(() => {
                setElapsedTime(Math.floor((new Date() - startTime) / 1000));
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setElapsedTime(0);
        }
    }, [activeTimer]);

    const handleStopTimer = async () => {
        if (!activeTimer) return;
        try {
            await axios.post(`${API}/time-entries/stop`, {
                entry_id: activeTimer.id
            }, {
                headers: getAuthHeaders()
            });
            toast.success('Timer stopped');
            setActiveTimer(null);
            fetchCases();
        } catch (error) {
            toast.error('Failed to stop timer');
        }
    };

    const handleAcknowledgeComplimentary = async () => {
        try {
            await axios.post(`${API}/subscription/acknowledge-complimentary`, {}, {
                headers: getAuthHeaders()
            });
            setShowComplimentaryPopup(false);
        } catch (error) {
            console.error('Failed to acknowledge');
            setShowComplimentaryPopup(false);
        }
    };

    const handleQuickAdd = () => {
        setIsQuickAddOpen(true);
        setQuickAddType('time');
    };

    const handleCreateCase = async () => {
        if (!newCaseName.trim()) {
            toast.error('Case name is required');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/cases`, {
                name: newCaseName,
                client_name: newClientName,
                case_type: newCaseType
            }, {
                headers: getAuthHeaders()
            });
            toast.success('Case created');
            setNewCaseName('');
            setNewClientName('');
            setNewCaseType('Other');
            setIsQuickAddOpen(false);
            fetchCases();
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error('Free accounts are limited to 1 case. Upgrade to Pro for unlimited cases!', {
                    action: {
                        label: 'Upgrade',
                        onClick: () => navigate('/profile')
                    },
                    duration: 5000
                });
            } else {
                toast.error('Failed to create case');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddManualTime = async () => {
        if (!selectedCase) {
            toast.error('Please select a case');
            return;
        }
        
        const entryData = {
            case_id: selectedCase,
            task_type: selectedTask,
            duration_minutes: dialMinutes,
            notes: manualNotes
        };
        
        setIsSubmitting(true);
        
        // If offline, queue for later sync
        if (!isOnline) {
            const success = await queueTimeEntry(entryData);
            if (success) {
                setIsQuickAddOpen(false);
                setManualNotes('');
            }
            setIsSubmitting(false);
            return;
        }
        
        // Online - submit directly
        try {
            await axios.post(`${API}/time-entries/manual`, entryData, {
                headers: getAuthHeaders()
            });
            toast.success(`${dialMinutes} minutes added`);
            setIsQuickAddOpen(false);
            setManualNotes('');
            fetchCases();
        } catch (error) {
            // If network error, try to queue offline
            if (!navigator.onLine || error.code === 'ERR_NETWORK') {
                const success = await queueTimeEntry(entryData);
                if (success) {
                    setIsQuickAddOpen(false);
                    setManualNotes('');
                }
            } else {
                toast.error('Failed to add time');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateTodo = async () => {
        if (!newTodoTitle.trim()) {
            toast.error('Please enter a todo title');
            return;
        }
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/todos`, {
                title: newTodoTitle.trim(),
                case_id: newTodoCase && newTodoCase !== 'none' ? newTodoCase : null,
                priority: newTodoPriority,
                due_date: newTodoDueDate || null
            }, { headers: getAuthHeaders() });
            
            toast.success('Todo created!');
            setNewTodoTitle('');
            setNewTodoCase('');
            setNewTodoPriority('medium');
            setNewTodoDueDate('');
            setIsQuickAddOpen(false);
            fetchTodos();
        } catch (error) {
            toast.error('Failed to create todo');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenCompleteTodo = (todo) => {
        setCompleteTodoModal(todo);
        setConvertToTime(false);
        setTodoTimeMinutes(30);
        setTodoTaskType(TASK_TYPES[0].code);
        setTodoNote(todo.title);
        setTodoDate(new Date().toISOString().split('T')[0]);
    };

    const handleCompleteTodo = async () => {
        if (!completeTodoModal) return;
        
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/todos/${completeTodoModal.id}/complete`, {
                convert_to_time: convertToTime,
                duration_minutes: convertToTime ? todoTimeMinutes : null,
                task_type: convertToTime ? todoTaskType : null,
                note: convertToTime ? todoNote : null,
                date: convertToTime ? todoDate : null
            }, { headers: getAuthHeaders() });
            
            toast.success(convertToTime ? 'Todo completed & time logged!' : 'Todo completed!');
            setCompleteTodoModal(null);
            fetchTodos();
            if (convertToTime) fetchCases();
        } catch (error) {
            toast.error('Failed to complete todo');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteTodo = async (todoId) => {
        try {
            await axios.delete(`${API}/todos/${todoId}`, { headers: getAuthHeaders() });
            toast.success('Todo deleted');
            fetchTodos();
        } catch (error) {
            toast.error('Failed to delete todo');
        }
    };

    const formatElapsed = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const formatTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) return `${hours}h ${mins}m`;
        return `${mins}m`;
    };

    // Prepare calendar markers - create a set for quick lookup
    const markedDatesMap = {};
    reminders.forEach(r => {
        const dateStr = r.due_date?.split('T')[0];
        if (dateStr) {
            if (!markedDatesMap[dateStr] || r.days_until_due < 0) {
                markedDatesMap[dateStr] = r.days_until_due < 0 ? 'overdue' : r.is_critical ? 'critical' : 'normal';
            }
        }
    });

    // Custom day render for calendar markers
    const modifiers = {
        hasReminder: (date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            return !!markedDatesMap[dateStr];
        },
        overdue: (date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            return markedDatesMap[dateStr] === 'overdue';
        },
        critical: (date) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            return markedDatesMap[dateStr] === 'critical';
        }
    };

    const modifiersStyles = {
        overdue: {
            backgroundColor: 'rgb(254, 226, 226)',
            color: 'rgb(185, 28, 28)',
            fontWeight: '600'
        },
        critical: {
            backgroundColor: 'rgb(254, 243, 199)',
            color: 'rgb(180, 83, 9)',
            fontWeight: '600'
        },
        hasReminder: {
            fontWeight: '600'
        }
    };

    // Get reminders for selected date
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    const remindersForDate = reminders.filter(r => 
        r.due_date?.startsWith(selectedDateStr)
    );

    // Filter active cases
    const activeCases = cases.filter(c => c.status !== 'Closed').slice(0, 5);
    const overdueReminders = reminders.filter(r => r.days_until_due < 0);
    const todayReminders = reminders.filter(r => r.days_until_due === 0);
    const upcomingWeekReminders = reminders.filter(r => r.days_until_due > 0 && r.days_until_due <= 7);

    const greeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    return (
        <div className="min-h-screen bg-background pb-safe">
            {/* Onboarding Tutorial */}
            {showTutorial && (
                <OnboardingTutorial onComplete={() => setShowTutorial(false)} />
            )}
            
            {/* Header */}
            <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm text-muted-foreground">{greeting()}</p>
                            <h1 className="font-heading text-2xl font-semibold truncate">{user?.name?.split(' ')[0] || 'Counselor'}</h1>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <OfflineIndicator 
                                isOnline={isOnline}
                                isSyncing={isSyncing}
                                pendingCount={pendingCount}
                                onSync={triggerSync}
                            />
                            {isAdmin && (
                                <button 
                                    onClick={() => navigate('/admin')}
                                    className="flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] aspect-square rounded-full bg-slate-900 hover:bg-slate-800 transition-colors text-white flex-shrink-0"
                                    title="Admin Dashboard"
                                    data-testid="admin-btn"
                                >
                                    <Shield className="h-4 w-4 flex-shrink-0" />
                                </button>
                            )}
                            <button 
                                onClick={() => {
                                    logout();
                                    navigate('/login');
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors text-slate-600"
                                title="Sign out"
                                data-testid="logout-btn"
                            >
                                <span className="text-sm font-medium">Log out</span>
                                <LogOut className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="px-6 py-4 space-y-6">
                {/* Combined Calendar & Schedule Card */}
                <section className="bg-white rounded-3xl shadow-soft overflow-hidden">
                    {/* Date Header - Always Visible */}
                    <button 
                        onClick={() => setIsCalendarExpanded(!isCalendarExpanded)}
                        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                        data-testid="calendar-toggle"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-[#1C6EE7] flex flex-col items-center justify-center text-white">
                                <span className="text-[10px] font-medium uppercase leading-none">{format(selectedDate, 'MMM')}</span>
                                <span className="text-lg font-bold leading-none">{format(selectedDate, 'd')}</span>
                            </div>
                            <div className="text-left">
                                <p className="font-heading font-semibold text-lg">
                                    {isToday(selectedDate) ? 'Today' : format(selectedDate, 'EEEE')}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                    {remindersForDate.length > 0 
                                        ? `${remindersForDate.length} item${remindersForDate.length > 1 ? 's' : ''} scheduled`
                                        : 'No deadlines'
                                    }
                                </p>
                            </div>
                        </div>
                        <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isCalendarExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Expandable Calendar */}
                    {isCalendarExpanded && (
                        <div className="px-4 pb-4 border-t border-slate-100 animate-fade-in-up">
                            <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={(date) => {
                                    if (date) {
                                        setSelectedDate(date);
                                        setIsCalendarExpanded(false);
                                    }
                                }}
                                modifiers={modifiers}
                                modifiersStyles={modifiersStyles}
                                className="w-full"
                                classNames={{
                                    months: "flex flex-col",
                                    month: "space-y-4 w-full",
                                    caption: "flex justify-center pt-3 relative items-center mb-2",
                                    caption_label: "text-base font-heading font-semibold",
                                    nav: "space-x-1 flex items-center",
                                    nav_button: "h-8 w-8 bg-transparent p-0 hover:bg-slate-100 rounded-full transition-colors inline-flex items-center justify-center",
                                    nav_button_previous: "absolute left-1",
                                    nav_button_next: "absolute right-1",
                                    table: "w-full border-collapse",
                                    head_row: "flex w-full justify-around",
                                    head_cell: "text-muted-foreground font-medium text-[0.75rem] py-2 w-10 text-center",
                                    row: "flex w-full justify-around mt-1",
                                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 h-10 w-10 flex items-center justify-center",
                                    day: "h-8 w-8 p-0 font-normal rounded-full hover:bg-slate-100 transition-colors inline-flex items-center justify-center text-sm",
                                    day_selected: "bg-[#1C6EE7] text-white hover:bg-[#1C6EE7] hover:text-white focus:bg-[#1C6EE7] focus:text-white rounded-full",
                                    day_today: "bg-blue-100 text-[#1C6EE7] font-semibold rounded-full",
                                    day_outside: "text-muted-foreground opacity-50",
                                    day_disabled: "text-muted-foreground opacity-50",
                                }}
                                data-testid="home-calendar"
                            />
                        </div>
                    )}

                    {/* Schedule Items - Below Date */}
                    {remindersForDate.length > 0 && (
                        <div className="px-4 pb-4 space-y-2 border-t border-slate-100">
                            {remindersForDate.slice(0, 3).map(reminder => (
                                <button
                                    key={reminder.id}
                                    onClick={() => navigate(`/case/${reminder.case_id}`)}
                                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left"
                                >
                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                        reminder.days_until_due < 0 ? 'bg-red-500' : 
                                        reminder.is_critical ? 'bg-amber-500' : 'bg-[#1C6EE7]'
                                    }`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{reminder.title}</p>
                                        <p className="text-xs text-muted-foreground truncate">{reminder.case_name}</p>
                                    </div>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                </button>
                            ))}
                            {remindersForDate.length > 3 && (
                                <button 
                                    onClick={() => navigate('/calendar')}
                                    className="w-full text-center text-sm text-primary font-medium py-2 hover:underline"
                                >
                                    +{remindersForDate.length - 3} more
                                </button>
                            )}
                        </div>
                    )}
                </section>

                {/* Active Timer Card */}
                {activeTimer && (
                    <section className="animate-fade-in-up">
                        <div className="bg-[#1C6EE7] rounded-3xl p-6 text-white timer-running">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <Timer className="h-5 w-5 animate-pulse" />
                                    <span className="font-medium">Timer Running</span>
                                </div>
                                <Badge className="bg-white/20 text-white border-0">
                                    {getTaskByCode(activeTimer.task_type)?.name || activeTimer.task_type}
                                </Badge>
                            </div>
                            <p className="font-display text-4xl font-bold tracking-tight mb-2">
                                {formatElapsed(elapsedTime)}
                            </p>
                            <p className="text-white/80 text-sm mb-4">{activeTimer.case_name}</p>
                            <button
                                onClick={handleStopTimer}
                                className="w-full py-3 bg-white text-[#1C6EE7] rounded-full font-semibold hover:bg-white/90 transition-all active:scale-95"
                                data-testid="stop-active-timer"
                            >
                                Stop Timer
                            </button>
                        </div>
                    </section>
                )}

                {/* Urgent Alerts */}
                {(overdueReminders.length > 0 || todayReminders.length > 0 || upcomingWeekReminders.length > 0) && !activeTimer && (
                    <section className="animate-fade-in-up">
                        <div className={`rounded-3xl p-4 ${
                            overdueReminders.length > 0 ? 'bg-red-50' : 
                            todayReminders.length > 0 ? 'bg-amber-50' : 'bg-blue-50'
                        }`}>
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${
                                    overdueReminders.length > 0 ? 'bg-red-100' : 
                                    todayReminders.length > 0 ? 'bg-amber-100' : 'bg-blue-100'
                                }`}>
                                    <AlertTriangle className={`h-4 w-4 ${
                                        overdueReminders.length > 0 ? 'text-red-600' : 
                                        todayReminders.length > 0 ? 'text-amber-600' : 'text-blue-600'
                                    }`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`font-medium text-sm ${
                                        overdueReminders.length > 0 ? 'text-red-800' : 
                                        todayReminders.length > 0 ? 'text-amber-800' : 'text-blue-800'
                                    }`}>
                                        {overdueReminders.length > 0 
                                            ? `${overdueReminders.length} overdue`
                                            : todayReminders.length > 0
                                                ? `${todayReminders.length} due today`
                                                : `${upcomingWeekReminders.length} due this week`
                                        }
                                    </p>
                                </div>
                                <button 
                                    onClick={() => navigate('/calendar')}
                                    className="text-xs font-medium text-primary"
                                >
                                    View
                                </button>
                            </div>
                        </div>
                    </section>
                )}

                {/* Active Cases / Todo Toggle */}
                <section>
                    <div className="flex items-center justify-between mb-4">
                        {/* Toggle between Active Cases and Todo */}
                        <div className="flex bg-slate-100 rounded-full p-1.5">
                            <button
                                onClick={() => setShowTodos(false)}
                                className={`px-5 py-2 rounded-full text-base font-bold transition-all ${
                                    !showTodos ? 'bg-[#1C6EE7] shadow-md text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                Active Cases
                            </button>
                            <button
                                onClick={() => setShowTodos(true)}
                                className={`px-5 py-2 rounded-full text-base font-bold transition-all flex items-center gap-2 ${
                                    showTodos ? 'bg-[#1C6EE7] shadow-md text-white' : 'text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                <ListTodo className="h-4 w-4" />
                                Todo
                                {todoCounts.total > 0 && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full min-w-[22px] text-center ${
                                        showTodos ? 'bg-white/20 text-white' : 'bg-[#1C6EE7] text-white'
                                    }`}>
                                        {todoCounts.total}
                                    </span>
                                )}
                            </button>
                        </div>
                        <button 
                            onClick={() => showTodos ? setShowTodos(false) : navigate('/cases')}
                            className="text-sm text-primary font-medium hover:underline"
                        >
                            {showTodos ? '' : 'See all'}
                        </button>
                    </div>
                    
                    {!showTodos ? (
                        // Active Cases View
                        <>
                            {isLoading ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="bg-white rounded-3xl p-4 shadow-soft animate-pulse">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 bg-slate-200 rounded-2xl" />
                                                <div className="flex-1 space-y-2">
                                                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                                                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                                                </div>
                                                <div className="h-6 w-16 bg-slate-100 rounded-full" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : activeCases.length > 0 ? (
                                <div className="space-y-3">
                                    {activeCases.map(caseItem => (
                                        <TaskCard
                                            key={caseItem.id}
                                            title={caseItem.name}
                                            subtitle={caseItem.client_name || caseItem.case_type}
                                            time={formatTime(caseItem.total_minutes)}
                                            status={caseItem.status}
                                            isRunning={caseItem.active_timer}
                                            isShared={caseItem.is_shared}
                                            ownerName={caseItem.is_shared ? caseItem.owner_name : null}
                                            hasTodos={todoCounts.case_counts[caseItem.id] > 0}
                                            todoCount={todoCounts.case_counts[caseItem.id] || 0}
                                            onClick={() => navigate(`/case/${caseItem.id}`)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-white rounded-3xl shadow-soft">
                                    <p className="text-muted-foreground">No active cases</p>
                                    <button 
                                        onClick={() => { setQuickAddType('case'); setIsQuickAddOpen(true); }}
                                        className="mt-3 text-primary font-medium hover:underline"
                                    >
                                        Create your first case
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        // Todo List View
                        <>
                            {isLoadingTodos ? (
                                <div className="space-y-3">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-16 bg-white rounded-2xl animate-pulse" />
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {/* Add Todo Button */}
                                    <button
                                        onClick={() => { setQuickAddType('todo'); setIsQuickAddOpen(true); }}
                                        className="w-full p-4 bg-white rounded-2xl shadow-soft border-2 border-dashed border-slate-200 hover:border-primary/50 hover:bg-primary/5 transition-all flex items-center justify-center gap-2 text-muted-foreground hover:text-primary"
                                    >
                                        <Plus className="h-5 w-5" />
                                        <span className="font-medium">Add Todo</span>
                                    </button>
                                    
                                    {/* Incomplete Todos */}
                                    {todos.filter(t => !t.completed).length > 0 ? (
                                        todos.filter(t => !t.completed).map(todo => (
                                            <div 
                                                key={todo.id}
                                                className="bg-white rounded-2xl shadow-soft p-4"
                                            >
                                                <div className="flex items-start gap-3">
                                                    <button
                                                        onClick={() => handleOpenCompleteTodo(todo)}
                                                        className="mt-0.5 p-1 rounded-lg hover:bg-primary/10 transition-colors"
                                                    >
                                                        <Square className="h-5 w-5 text-slate-400" />
                                                    </button>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium text-foreground">{todo.title}</p>
                                                        {todo.case_name && (
                                                            <p className="text-sm text-muted-foreground mt-0.5">
                                                                {todo.case_name}
                                                            </p>
                                                        )}
                                                        <div className="flex items-center gap-2 mt-2">
                                                            {todo.priority === 'high' && (
                                                                <Badge variant="destructive" className="text-xs">High</Badge>
                                                            )}
                                                            {todo.priority === 'low' && (
                                                                <Badge variant="secondary" className="text-xs">Low</Badge>
                                                            )}
                                                            {todo.due_date && (
                                                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                                    <CalendarIcon className="h-3 w-3" />
                                                                    {new Date(todo.due_date).toLocaleDateString()}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleDeleteTodo(todo.id)}
                                                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 bg-white rounded-2xl shadow-soft">
                                            <CheckSquare className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                                            <p className="text-muted-foreground">No pending todos</p>
                                            <p className="text-sm text-slate-400 mt-1">You're all caught up!</p>
                                        </div>
                                    )}
                                    
                                    {/* Completed Todos (collapsed) */}
                                    {todos.filter(t => t.completed).length > 0 && (
                                        <div className="mt-4">
                                            <p className="text-sm font-medium text-muted-foreground mb-2 px-1">
                                                Completed ({todos.filter(t => t.completed).length})
                                            </p>
                                            {todos.filter(t => t.completed).slice(0, 3).map(todo => (
                                                <div 
                                                    key={todo.id}
                                                    className="bg-slate-50 rounded-xl p-3 mb-2 opacity-60"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <CheckSquare className="h-4 w-4 text-emerald-500" />
                                                        <p className="text-sm text-muted-foreground line-through flex-1">{todo.title}</p>
                                                        <button
                                                            onClick={() => handleDeleteTodo(todo.id)}
                                                            className="p-1 rounded hover:bg-slate-200 text-slate-400"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </section>

                {/* Spacer for bottom nav */}
                <div className="h-24" />
            </main>

            {/* Bottom Navigation */}
            <BottomNav onQuickAdd={handleQuickAdd} />

            {/* Quick Add Dialog */}
            <Dialog open={isQuickAddOpen} onOpenChange={setIsQuickAddOpen}>
                <DialogContent className="max-h-[85vh] overflow-y-auto ">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">
                            {quickAddType === 'case' ? 'New Case' : quickAddType === 'time' ? 'Log Time' : quickAddType === 'todo' ? 'New Todo' : 'New Reminder'}
                        </DialogTitle>
                        <DialogDescription>
                            {quickAddType === 'time' ? 'Add time to a case' : quickAddType === 'todo' ? 'Create a task to complete' : 'Create a new case to track'}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Quick Add Type Switcher */}
                    <div className="flex gap-2 mb-4">
                        {[
                            { key: 'time', label: 'Log Time', icon: Clock },
                            { key: 'case', label: 'New Case', icon: Plus },
                            { key: 'todo', label: 'Todo', icon: ListTodo },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setQuickAddType(tab.key)}
                                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-medium transition-all ${
                                    quickAddType === tab.key 
                                        ? 'bg-primary text-white' 
                                        : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                                }`}
                            >
                                <tab.icon className="h-4 w-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {quickAddType === 'todo' ? (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Todo Title</Label>
                                <Input
                                    value={newTodoTitle}
                                    onChange={(e) => setNewTodoTitle(e.target.value)}
                                    placeholder="What needs to be done?"
                                    className="h-12 rounded-xl"
                                    data-testid="todo-title-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <Label>Link to Case (Optional)</Label>
                                <Select value={newTodoCase} onValueChange={setNewTodoCase}>
                                    <SelectTrigger className="h-12 rounded-xl">
                                        <SelectValue placeholder="No case selected" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No case</SelectItem>
                                        {cases.filter(c => c.status !== 'Closed').map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Priority</Label>
                                    <Select value={newTodoPriority} onValueChange={setNewTodoPriority}>
                                        <SelectTrigger className="h-12 rounded-xl">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Due Date (Optional)</Label>
                                    <Input
                                        type="date"
                                        value={newTodoDueDate}
                                        onChange={(e) => setNewTodoDueDate(e.target.value)}
                                        className="h-12 rounded-xl"
                                    />
                                </div>
                            </div>
                            
                            <Button
                                onClick={handleCreateTodo}
                                disabled={isSubmitting || !newTodoTitle.trim()}
                                className="w-full h-12 rounded-xl"
                                data-testid="create-todo-btn"
                            >
                                {isSubmitting ? 'Creating...' : 'Create Todo'}
                            </Button>
                        </div>
                    ) : quickAddType === 'time' ? (
                        <div className="space-y-6">
                            {/* Touch Dial */}
                            <TouchDial 
                                value={dialMinutes}
                                onChange={setDialMinutes}
                                maxMinutes={480}
                            />

                            {/* Case Selection */}
                            <div className="space-y-2">
                                <Label>Select Case</Label>
                                <Select value={selectedCase} onValueChange={setSelectedCase}>
                                    <SelectTrigger className="rounded-xl h-12 w-full" data-testid="case-select">
                                        <SelectValue placeholder="Choose a case" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cases.filter(c => c.status !== 'Closed').map(c => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Task Type */}
                            <div className="space-y-2">
                                <Label>Task Type</Label>
                                <Select value={selectedTask} onValueChange={setSelectedTask}>
                                    <SelectTrigger className="rounded-xl h-12 w-full" data-testid="task-select">
                                        <SelectValue>
                                            {getTaskByCode(selectedTask) && (
                                                <span className="font-medium">{getTaskLabel(getTaskByCode(selectedTask))}</span>
                                            )}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[300px]">
                                        {TASK_TYPES.map(t => (
                                            <SelectItem key={t.code} value={t.code} className="py-2.5">
                                                <span className="font-medium">{t.code} - {t.name}</span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {getTaskByCode(selectedTask) && (
                                    <p className="text-xs text-muted-foreground px-1">
                                        {getTaskByCode(selectedTask).description}
                                    </p>
                                )}
                            </div>

                            {/* Notes with Voice Input */}
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    Notes (optional)
                                    <span className="text-xs text-muted-foreground">• tap mic to dictate</span>
                                </Label>
                                <VoiceInputInline
                                    value={manualNotes}
                                    onChange={setManualNotes}
                                    placeholder="What did you work on?"
                                />
                            </div>

                            <Button
                                onClick={handleAddManualTime}
                                disabled={isSubmitting || !selectedCase}
                                className="w-full h-14 rounded-full text-lg font-semibold"
                                data-testid="submit-time-btn"
                            >
                                {isSubmitting ? 'Adding...' : `Add ${dialMinutes} Minutes`}
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Case Name</Label>
                                <Input
                                    placeholder="Smith vs. Johnson"
                                    value={newCaseName}
                                    onChange={(e) => setNewCaseName(e.target.value)}
                                    className="rounded-xl h-12 w-full"
                                    data-testid="case-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Client Name</Label>
                                <Input
                                    placeholder="John Smith"
                                    value={newClientName}
                                    onChange={(e) => setNewClientName(e.target.value)}
                                    className="rounded-xl h-12 w-full"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Case Type</Label>
                                <Select value={newCaseType} onValueChange={setNewCaseType}>
                                    <SelectTrigger className="rounded-xl h-12 w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[40vh] overflow-y-auto">
                                        {caseTypes.map(t => (
                                            <SelectItem key={t} value={t}>{t}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <Button
                                onClick={handleCreateCase}
                                disabled={isSubmitting || !newCaseName}
                                className="w-full h-14 rounded-full text-lg font-semibold"
                                data-testid="submit-case-btn"
                            >
                                {isSubmitting ? 'Creating...' : 'Create Case'}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Complimentary Subscription Popup */}
            <Dialog open={showComplimentaryPopup} onOpenChange={setShowComplimentaryPopup}>
                <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-3xl text-center">
                    <div className="py-4">
                        <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center">
                            <Gift className="h-10 w-10 text-violet-600" />
                        </div>
                        <DialogHeader className="text-center">
                            <DialogTitle className="text-2xl font-bold text-center">
                                🎉 You Have Received a Gift!
                            </DialogTitle>
                            <DialogDescription className="text-center mt-2">
                                You have been granted a complimentary Pro subscription with unlimited cases.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-6 p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl border border-violet-200">
                            <p className="text-sm text-violet-700">Your complimentary access is active until:</p>
                            <p className="text-xl font-bold text-violet-900 mt-1">
                                {complimentaryEndDate 
                                    ? new Date(complimentaryEndDate).toLocaleDateString('en-US', { 
                                        month: 'long', 
                                        day: 'numeric', 
                                        year: 'numeric' 
                                    })
                                    : '6 months from now'
                                }
                            </p>
                        </div>
                        <Button
                            onClick={handleAcknowledgeComplimentary}
                            className="w-full mt-6 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-semibold"
                        >
                            Awesome, Thanks!
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Complete Todo Modal */}
            <Dialog open={!!completeTodoModal} onOpenChange={(open) => !open && setCompleteTodoModal(null)}>
                <DialogContent className="max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Complete Todo</DialogTitle>
                        <DialogDescription>
                            {completeTodoModal?.title}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {/* Show case info if linked */}
                        {completeTodoModal?.case_id && (
                            <div className="p-3 bg-slate-50 rounded-xl">
                                <p className="text-sm text-muted-foreground">Linked to case:</p>
                                <p className="font-medium text-foreground">{completeTodoModal?.case_name}</p>
                            </div>
                        )}

                        {/* Time entry fields - show when Bill Time mode is active */}
                        {convertToTime && completeTodoModal?.case_id && (
                            <div className="space-y-4 p-4 bg-[#1C6EE7]/5 rounded-xl border border-[#1C6EE7]/20">
                                <p className="text-sm font-medium text-[#1C6EE7]">Log Billable Time</p>
                                
                                <div className="space-y-2">
                                    <Label>Time Spent (minutes)</Label>
                                    <div className="flex items-center gap-2">
                                        {[15, 30, 60, 90].map(mins => (
                                            <button
                                                key={mins}
                                                onClick={() => setTodoTimeMinutes(mins)}
                                                className={`flex-1 py-2 rounded-lg font-medium text-sm transition-all ${
                                                    todoTimeMinutes === mins
                                                        ? 'bg-[#1C6EE7] text-white'
                                                        : 'bg-white border hover:border-[#1C6EE7]'
                                                }`}
                                            >
                                                {mins}m
                                            </button>
                                        ))}
                                    </div>
                                    <Input
                                        type="number"
                                        value={todoTimeMinutes}
                                        onChange={(e) => setTodoTimeMinutes(parseInt(e.target.value) || 0)}
                                        placeholder="Custom minutes"
                                        className="h-10 rounded-lg mt-2"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Task Type</Label>
                                    <Select value={todoTaskType} onValueChange={setTodoTaskType}>
                                        <SelectTrigger className="h-10 rounded-lg">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TASK_TYPES.map(task => (
                                                <SelectItem key={task.code} value={task.code}>
                                                    {task.code} - {task.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Note (appears on bill)</Label>
                                    <Input
                                        value={todoNote}
                                        onChange={(e) => setTodoNote(e.target.value)}
                                        placeholder="Description of work done"
                                        className="h-10 rounded-lg"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Date</Label>
                                    <Input
                                        type="date"
                                        value={todoDate}
                                        onChange={(e) => setTodoDate(e.target.value)}
                                        className="h-10 rounded-lg"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex gap-3 pt-2">
                            {convertToTime ? (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => setConvertToTime(false)}
                                        className="flex-1 h-11 rounded-xl"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        onClick={handleCompleteTodo}
                                        disabled={isSubmitting || todoTimeMinutes <= 0}
                                        className="flex-1 h-11 rounded-xl bg-[#1C6EE7] hover:bg-[#1559c7]"
                                        data-testid="bill-time-submit-btn"
                                    >
                                        {isSubmitting ? 'Billing...' : 'Bill Time'}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setConvertToTime(false);
                                            handleCompleteTodo();
                                        }}
                                        disabled={isSubmitting}
                                        className="flex-1 h-11 rounded-xl"
                                        data-testid="mark-complete-btn"
                                    >
                                        {isSubmitting ? 'Completing...' : 'Mark Complete'}
                                    </Button>
                                    {completeTodoModal?.case_id && (
                                        <Button
                                            onClick={() => setConvertToTime(true)}
                                            className="flex-1 h-11 rounded-xl bg-[#1C6EE7] hover:bg-[#1559c7]"
                                            data-testid="bill-time-btn"
                                        >
                                            Bill Time
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
